
/** `DWORD FONT_WIDTH = 30;` (`InstanceBaseEffect.cpp:118`). One legacy unit is
 *  10 mm, but this one is a screen-space step applied in the camera's own basis,
 *  so it is carried as the original's number and scaled at the draw call. */
export const DAMAGE_FONT_WIDTH = 30;

/** `if (index > 7) { TraceError(...); break; }` (`:184-188`). */
export const MAX_DAMAGE_DIGITS = 8;

/** Which sprite family a resolution draws in. `bSelf` in `ProcessDamage`. */
export type DamageSide = "self" | "target" | "nontarget";

/** The prefix on every texture name in the family (`:151-178`). */
export function damageTexturePrefix(side: DamageSide): string {
  return side === "self" ? "damage_" : side === "target" ? "target_" : "nontarget_";
}

export function damageSide(victimIsSelf: boolean, victimIsTarget: boolean): DamageSide {
  if (victimIsSelf) return "self";
  return victimIsTarget ? "target" : "nontarget";
}

/** A dodge or a block: `(flag & DAMAGE_DODGE) || (flag & DAMAGE_BLOCK)`. */
export interface DamageDraw {
  /** Texture stems, in the original's emission order: units digit first. Empty
   *  for a miss, for zero damage, and for a `nontarget_` resolution. */
  readonly digits: readonly string[];
  /** The single miss sprite, or null when the blow landed. */
  readonly miss: string | null;
}

export function damageDraw(
  damage: number,
  flags: { readonly missed?: boolean; readonly blocked?: boolean },
  side: DamageSide,
): DamageDraw {
  // `:126-133` -- the miss word replaces the number entirely, and the sprite is
  // picked by side, not by which of the two flags was set.
  if (flags.missed === true || flags.blocked === true) {
    if (side === "nontarget") return { digits: [], miss: null };
    return { digits: [], miss: side === "self" ? "damage_miss" : "target_miss" };
  }
  // `:171-176` -- queued, then dropped before any effect is created.
  if (side === "nontarget") return { digits: [], miss: null };

  const prefix = damageTexturePrefix(side);
  const digits: string[] = [];
  let remaining = Math.max(0, Math.trunc(damage));
  while (remaining > 0) {
    if (digits.length >= MAX_DAMAGE_DIGITS) break;
    digits.push(`${prefix}${remaining % 10}`);
    remaining = Math.floor(remaining / 10);
  }
  return { digits, miss: null };
}

/** Where digit `index` sits relative to the anchor, in legacy screen units.
 *  Negative because `matTrans._41` is negated (`:202`): the units digit is at 0
 *  and each further digit steps left. */
export function damageDigitOffset(index: number): number {
  return index === 0 ? 0 : -DAMAGE_FONT_WIDTH * index;
}

export const DAMAGE_DIGIT_LIFETIME_MS = 500;

const ALPHA_TABLE: readonly (readonly [number, number])[] = [
  [0.011429, 0.701031],
  [0.382857, 1.0],
  [0.605714, 1.0],
  [1.0, 0.041237],
];

export function damageDigitAlpha(phase: number): number {
  return sampleDamageCurve(ALPHA_TABLE, phase <= 0 ? 0 : phase >= 1 ? 1 : phase);
}


/** `[x, y, width, height]` in the atlas, in atlas pixels. */
export type DamageGlyphCell = readonly [number, number, number, number];

/** `[time, value]` rows, read the way `EffectLib/Type.h:227` reads them:
 *  linear between neighbours, flat outside the first and last. */
export type DamageCurve = readonly (readonly [number, number])[];

/** One sprite family: a quad and the curves it plays over a particle's life. */
export interface DamageDigitFamily {
  /** On-screen quad, millimetres, before the scale curves. */
  readonly widthMm: number;
  readonly heightMm: number;
  /** The step between adjacent digits: `FONT_WIDTH` in millimetres. */
  readonly stepMm: number;
  readonly lifetimeSeconds: number;
  /** `TimeEventScaleY`/`TimeEventScaleX`, named by what they scale on screen
   *  rather than by the axis the file calls them. */
  readonly scaleWidth: DamageCurve;
  readonly scaleHeight: DamageCurve;
  readonly alpha: DamageCurve;
}

export interface DamageDigitManifest {
  readonly atlas: { readonly image: string; readonly width: number; readonly height: number };
  readonly glyphs: Readonly<Record<string, DamageGlyphCell>>;
  readonly families: Readonly<Record<string, DamageDigitFamily>>;
}

/** The manifest plus the decoded sheet, once both have arrived. */
export interface DamageDigitAtlas {
  readonly manifest: DamageDigitManifest;
  readonly image: CanvasImageSource;
}

let installed: DamageDigitAtlas | null = null;

/** The family a texture stem belongs to. The miss words are their own family
 *  -- `miss.mse` and `target_miss.mse` are separate registrations with their
 *  own quad (`playersettingmodule.py:247-248`) -- and every other stem is a
 *  digit of the prefix it starts with. */
export function damageGlyphFamily(stem: string): string {
  if (stem.endsWith("_miss")) return stem;
  const cut = stem.lastIndexOf("_");
  return cut < 0 ? stem : stem.slice(0, cut + 1);
}

/** Whether a value has the shape this module reads. A manifest that is merely
 *  *present* is not enough: a truncated or half-written file must fall back to
 *  the text path rather than draw a run of blanks. */
function usable(value: unknown): value is DamageDigitManifest {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as DamageDigitManifest;
  const atlas: unknown = candidate.atlas;
  if (typeof atlas !== "object" || atlas === null) return false;
  if (typeof candidate.glyphs !== "object" || candidate.glyphs === null) return false;
  if (typeof candidate.families !== "object" || candidate.families === null) return false;
  // Every stem `damageDraw` can name has to be there, or some hits would draw
  // and others would not -- worse than none drawing.
  for (const prefix of ["damage_", "target_"]) {
    if (candidate.families[prefix] === undefined) return false;
    for (let digit = 0; digit < 10; digit += 1) {
      if (!Array.isArray(candidate.glyphs[`${prefix}${String(digit)}`])) return false;
    }
    const miss = `${prefix}miss`;
    if (!Array.isArray(candidate.glyphs[miss]) || candidate.families[miss] === undefined) {
      return false;
    }
  }
  return true;
}

/** Install a manifest and its sheet. Returns whether the pair is now usable;
 *  passing `null` for either uninstalls, which is how a test restores the
 *  text path. */
export function setDamageDigitAtlas(
  manifest: unknown,
  image: CanvasImageSource | null,
): boolean {
  if (manifest === null || image === null || !usable(manifest)) {
    installed = null;
    return false;
  }
  installed = { manifest, image };
  return true;
}

/** The installed atlas, or null while it is still loading or has failed. */
export function damageDigitAtlas(): DamageDigitAtlas | null {
  return installed;
}

/** The atlas rectangle for a texture stem, or null when the sheet has no such
 *  glyph -- which is how a `nontarget_` stem, or a manifest built against a
 *  different pack, declines rather than draws the wrong picture. */
export function damageGlyphCell(stem: string): DamageGlyphCell | null {
  const cell = installed?.manifest.glyphs[stem];
  return cell === undefined ? null : cell;
}

export const DAMAGE_DIGIT_MANIFEST_URL = "damagedigits.json";

/** Fetch the manifest and its sheet. Resolves to whether digits are now
 *  available; never rejects -- a client that cannot reach the atlas keeps the
 *  text floaters rather than losing damage feedback entirely. */
export async function loadDamageDigits(
  url: string = DAMAGE_DIGIT_MANIFEST_URL,
  decode: (src: string) => Promise<CanvasImageSource> = defaultDecode,
): Promise<boolean> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${url}: HTTP ${String(response.status)}`);
    const manifest: unknown = await response.json();
    if (!usable(manifest)) throw new Error(`${url}: not the shape this reads`);
    // Resolved against the manifest rather than against the page: both live in
    // `public/`, and a client served from a sub-path must not go looking at
    // the origin's root for the sheet.
    const sheet = new URL(manifest.atlas.image, new URL(url, document.baseURI)).href;
    return setDamageDigitAtlas(manifest, await decode(sheet));
  } catch (error) {
    console.warn("damage digit atlas unavailable; drawing numbers as text", error);
    setDamageDigitAtlas(null, null);
    return false;
  }
}

async function defaultDecode(src: string): Promise<CanvasImageSource> {
  const image = new Image();
  image.src = src;
  await image.decode();
  return image;
}

export function sampleDamageCurve(curve: DamageCurve, t: number): number {
  if (curve.length === 0) return 1;
  const first = curve[0]!;
  const last = curve[curve.length - 1]!;
  if (t <= first[0]) return first[1];
  if (t >= last[0]) return last[1];
  for (let i = 1; i < curve.length; i += 1) {
    const a = curve[i - 1]!;
    const b = curve[i]!;
    if (t <= b[0]) {
      const span = b[0] - a[0];
      return span <= 0 ? b[1] : a[1] + ((t - a[0]) / span) * (b[1] - a[1]);
    }
  }
  return last[1];
}
